import { AuthService } from 'services/auth';
import { getText, LanguageService } from 'services/languageService';
import { setNeedUpdateInfoAction } from 'store/actions/layers';
import { setLastAteParams } from 'store/actions/layers/constants';
import { AppDispatch } from 'store/index';
import { layersSlice } from 'store/reducers/layers';
import { mainPageSlice } from 'store/reducers/mainPage';
import {
  ISelectedATE,
  ISelectLayer,
  ISelectLayersATE,
  IStatDataset,
  MainLangType,
} from 'types/mainPage';
import { deleteCookie, setCookie } from 'utils/cookie';
import { httpClient, httpConfig } from 'utils/httpClient';
import { removeAccessItems, setLocalAccessToken } from 'utils/localStorage';
import { TabType } from 'components/UI/StatisticsSelects/constants';
import { getClusterInfoVL } from '../vectorLayers/constants';
import { updateClusterInfoVL } from '../vectorLayers';
const URL = process.env.REACT_APP_API_URL;

export const setMainPageLangAction =
  (lang: MainLangType) => (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.setMainLang(lang));
    LanguageService.setLanguage(lang);
    dispatch(mainPageSlice.actions.setNeedUpdate(true));
  };

export const setStatisticsErrorMsgAction =
  (msg: string) => (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.setStatisticsErrorMsg(msg));
  };

export const setOpenToolbarAction =
  (open: boolean) => (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.setOpenToolbar(open));
  };

export const setOpenSubstrModalAction =
  (open: boolean) => (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.setOpenSubstrateModal(open));
  };

export const toggleOpenLayerModalAction = () => (dispatch: AppDispatch) => {
  dispatch(mainPageSlice.actions.toggleOpenLayerInfo());
};

export const toggleOpenClusterModalAction =
  (open: boolean) => (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.toggleOpenClusterInfo(open));
  };

export const logInAction =
  (data: { login: string; password: string }) =>
  async (dispatch: AppDispatch) => {
    const { signIn } = AuthService;
    dispatch(mainPageSlice.actions.setLoading(true));
    try {
      const res = await signIn(data);
      const acToken = res.data['authorisation']['access_token'];
      const user = res.data['user'];

      setLocalAccessToken(acToken);
      setCookie('user', JSON.stringify(user), 1);
      location.reload();
    } catch (e: any) {
      dispatch(mainPageSlice.actions.logInError());
    }
  };

export const logOutAction = () => async (dispatch: AppDispatch) => {
  try {
    const url = `${URL}/api/logout`;
    const response = await httpClient.post(url);

    if (response.statusText === 'OK') {
      removeAccessItems();
      deleteCookie('user');
      dispatch(mainPageSlice.actions.setLoginState(false));
      location.reload();
    }
  } catch (e) {
    console.log(e);
  } finally {
    dispatch(mainPageSlice.actions.setLogoutOpen(false));
  }
};

export const resetLogInErrorAction = () => (dispatch: AppDispatch) => {
  dispatch(mainPageSlice.actions.resetError());
};

export const setLoginLoadingAction =
  (load: boolean) => (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.setLoading(load));
  };

export const setOpenLoginAction =
  (open: boolean) => (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.setLoginOpen(open));
  };

export const setOpenLogoutAction =
  (open: boolean) => (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.setLogoutOpen(open));
  };

export const setBackdropLoadingAction =
  (loading: boolean) => (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.setBackdropLoading(loading));
  };

export const setExportingStatsAction =
  (exporting: boolean, ext: string) => (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.setExporting({ exporting, ext }));
  };

export const getToolbarSelectAction =
  (isLog: boolean, year = '') =>
  async (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.setSelectLoading(true));
    let currentYear = year;

    if (!currentYear) {
      const yearUrl = `${URL}/api/${isLog ? 'pv-' : ''}years`;
      const res = await httpClient.post(yearUrl);
      const _years = res.data.data;
      const defaultYear = _years.find((item) => item.default);
      currentYear = defaultYear.year;
      const years = _years.map((item) => item.year);
      dispatch(layersSlice.actions.setYears(years));
    }
    dispatch(layersSlice.actions.setCurrentYear(currentYear));

    const lang = LanguageService.getLanguage();
    try {
      const url = `${URL}/api/${isLog ? 'pv-' : ''}layers`;
      const res = await httpClient.post(
        url,
        new URLSearchParams({ lang, list: '1', year: currentYear }),
        httpConfig
      );

      const _select = res.data.data;
      const select = _select.map((item) => {
        return { ...item, label: item.name, value: String(item.id) };
      });

      dispatch(mainPageSlice.actions.setToolbarSelect(select));
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(mainPageSlice.actions.setSelectLoading(false));
    }
  };

export const resetToolbarSelect = () => (dispatch: AppDispatch) => {
  dispatch(mainPageSlice.actions.resetToolbarSelect());
};

export const getStatAteSelectAction =
  (ate: ISelectLayersATE[]) => async (dispatch: AppDispatch) => {
    let options: ISelectLayersATE[];

    if (ate.length) {
      options = ate;
    } else {
      const ateUrl = `${URL}/api/listate`;

      const lang = LanguageService.getLanguage();
      const resAte = await httpClient.post(
        ateUrl,
        new URLSearchParams({ lang }),
        httpConfig
      );
      const listAte = resAte.data.data;

      options = listAte.map((item) => {
        return { ...item, label: item.name };
      });
    }

    dispatch(
      mainPageSlice.actions.setStatSelect({
        select: 'statisticsAte',
        options: options,
      })
    );
  };

export const getStatDatasetSelectAction =
  (isLog: boolean, year = '', type: TabType, stopLoader = true) =>
  async (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.setBackdropLoading(true));
    let currentYear = year;

    if (!currentYear) {
      const yearUrl = `${URL}/api/${isLog ? 'pv-' : ''}years`;
      const res = await httpClient.post(yearUrl);
      const _years = res.data.data;
      const defaultYear = _years.find((item) => item.default);
      currentYear = defaultYear.year;
      const years = _years.map((item) => item.year);
      dispatch(layersSlice.actions.setYears(years));
    }
    dispatch(layersSlice.actions.setCurrentYear(currentYear));

    const url = `${URL}/api/${isLog ? 'pv-' : ''}datasets`;
    const lang = LanguageService.getLanguage();
    const res = await httpClient.post(
      url,
      new URLSearchParams({ lang, year, type }),
      httpConfig
    );

    const options = res.data.data.map((item) => {
      return { ...item, label: item.name };
    });

    dispatch(
      mainPageSlice.actions.setStatSelect({
        select: 'statisticsDatasets',
        options: options,
      })
    );
    if (stopLoader) {
      dispatch(mainPageSlice.actions.setBackdropLoading(false));
    }

    return options;
  };

export const getStatLayersSelectAction =
  (isLog: boolean, dataset: number, year = '', isOpenFoundInfo: boolean) =>
  async (dispatch: AppDispatch) => {
    const url = `${URL}/api/${isLog ? 'pv-' : ''}layers`;
    const lang = LanguageService.getLanguage();
    const res = await httpClient.post(
      url,
      new URLSearchParams({ lang, dataset: String(dataset), list: '1', year }),
      httpConfig
    );

    const options = res.data.data.map((item) => {
      return { ...item, label: item.name };
    });

    dispatch(
      mainPageSlice.actions.setStatSelect({
        select: 'statisticsLayers',
        options: options,
      })
    );

    // update info modal after changing lang
    if (isOpenFoundInfo) {
      dispatch(setNeedUpdateInfoAction(true));
    }
  };

export const setCurrentStatAction =
  (
    param: string,
    value: ISelectLayersATE[] | IStatDataset | ISelectLayer | null | undefined
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(
      mainPageSlice.actions.setCurrentStat({
        param,
        value,
      })
    );
  };

export const getStatDataAction =
  (isLog: boolean, params: { [key: string]: string }) =>
  async (dispatch: AppDispatch) => {
    dispatch(setBackdropLoadingAction(true));
    const url = `${URL}/api/${isLog ? 'pv-' : ''}statistics`;

    try {
      const res = await httpClient.post(
        url,
        new URLSearchParams(params),
        httpConfig
      );

      const { data, export: exportInfo, wkts: wkt } = res.data.data;

      const updateData = data.map((item, index) => ({
        ...item,
        paramName: item.key,
        paramValue: item.value,
        id: item.key + index,
        color: item.color,
      }));

      if (updateData.length) {
        dispatch(
          mainPageSlice.actions.setStatisticsData({
            data: updateData,
            export: exportInfo,
            wkt,
          })
        );
        dispatch(mainPageSlice.actions.setEmptyStatisticsData(false));
      } else {
        dispatch(mainPageSlice.actions.setEmptyStatisticsData(true));
        dispatch(mainPageSlice.actions.resetStatisticsData());
      }
    } catch (e: any) {
      const msg = e.response.data.message;
      dispatch(setStatisticsErrorMsgAction(msg));
      dispatch(mainPageSlice.actions.resetStatisticsData());
    } finally {
      dispatch(setBackdropLoadingAction(false));
    }
  };

export const setEmptyStatisticsDataAction =
  (show: boolean) => async (dispatch: AppDispatch) => {
    dispatch(mainPageSlice.actions.setEmptyStatisticsData(show));
  };

export const updateStatDataAction =
  (
    isLog: boolean,
    type: TabType,
    params?: { [key: string]: string },
    year?: string
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(setBackdropLoadingAction(true));
    const datasets = await dispatch(
      getStatDatasetSelectAction(isLog, year, type)
    );

    if (params) {
      await dispatch(getStatDataAction(isLog, params));
    }
    dispatch(setBackdropLoadingAction(false));
    dispatch(mainPageSlice.actions.setNeedUpdate(false));

    return datasets;
  };

export const resetStatSelectsAction = () => (dispatch: AppDispatch) => {
  dispatch(mainPageSlice.actions.resetStatSelects());
};

export const resetStatDataAction = () => (dispatch: AppDispatch) => {
  dispatch(mainPageSlice.actions.resetStatisticsData());
};

export const resetStatisticsDataset = () => (dispatch: AppDispatch) => {
  dispatch(mainPageSlice.actions.resetStatisticsDataset());
};

export const needUpdateAction = (need: boolean) => (dispatch: AppDispatch) => {
  dispatch(mainPageSlice.actions.setNeedUpdate(need));
};

export const langUpdateStatAteLayerAction =
  (
    region: ISelectedATE[] | null,
    city: ISelectedATE[] | null,
    vc: ISelectedATE[] | null
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(setBackdropLoadingAction(true));
    const ateUrl = `${URL}/api/listate`;
    const lang = LanguageService.getLanguage();
    const allOptions = {
      id: 'all',
      label: getText('SELECT_ALL'),
      name: getText('SELECT_ALL'),
      type: '',
      dataset_name: '',
    };

    const resRegion = await httpClient.post(
      ateUrl,
      new URLSearchParams({ lang }),
      httpConfig
    );

    const listRegion = resRegion.data.data.map((item) => {
      return { ...item, label: item.name };
    });
    const fullListRegion = [allOptions, ...listRegion];

    dispatch(
      layersSlice.actions.setAte({ param: 'ateRegions', ate: fullListRegion })
    );

    if (region?.length) {
      const params = setLastAteParams(
        {
          regions: region,
          cities: city,
          vc: vc,
        },
        true
      );
      const resCities = await httpClient.post(
        ateUrl,
        new URLSearchParams(params),
        httpConfig
      );

      const listCities = resCities.data.data.map((item) => {
        return { ...item, label: item.name };
      });
      const fullListCities = [allOptions, ...listCities];

      const currentRegions: any = [];

      region.forEach((reg) => {
        fullListRegion.forEach((listReg) => {
          if (reg.id === listReg.id && reg.type === listReg.type) {
            currentRegions.push(listReg);
          }
        });
      });

      dispatch(
        layersSlice.actions.setAte({ param: 'ateCities', ate: fullListCities })
      );
      dispatch(
        mainPageSlice.actions.setCurrentStat({
          param: 'currentAteRegions',
          value: currentRegions,
        })
      );

      if (city?.length) {
        const currentCities: any = [];

        city.forEach((cit) => {
          fullListCities.forEach((listCit) => {
            if (cit.id === listCit.id && cit.type === listCit.type) {
              currentCities.push(listCit);
            }
          });
        });

        dispatch(
          mainPageSlice.actions.setCurrentStat({
            param: 'currentAteCities',
            value: currentCities,
          })
        );
      }
    }

    if (city?.length) {
      const params = setLastAteParams({
        regions: region,
        cities: city,
        vc: vc,
      });

      const resVC = await httpClient.post(
        ateUrl,
        new URLSearchParams(params),
        httpConfig
      );

      const listVC = resVC.data.data.map((item) => {
        return { ...item, label: item.name };
      });
      const fullListVC = [allOptions, ...listVC];

      dispatch(layersSlice.actions.setAte({ param: 'ateVC', ate: fullListVC }));

      if (vc?.length) {
        const currentVC: any = [];

        vc.forEach((curVc) => {
          fullListVC.forEach((newVc) => {
            if (curVc.id === newVc.id && curVc.type === newVc.type) {
              currentVC.push(newVc);
            }
          });
        });

        dispatch(
          mainPageSlice.actions.setCurrentStat({
            param: 'currentAteVC',
            value: currentVC,
          })
        );
      }
    }

    dispatch(setBackdropLoadingAction(false));
    dispatch(needUpdateAction(false));
  };

export const setCurrentTabAction = (tab: string) => (dispatch: AppDispatch) => {
  dispatch(mainPageSlice.actions.setCurrentsTab(tab));
};
